// Shared constants for Redbird Markets

// Official ISU Colors (based on brand guidelines)
export const COLORS = {
  // Primary ISU Colors
  primary: '#CE1126',        // Official ISU Red (Pantone 186C)
  primaryDark: '#a00f20',    // Darker red for hover states
  secondary: '#000000',      // Official ISU Black
  
  // Supporting Colors
  accent: '#F9D016',         // ISU Yellow ("Redbird Yellow")
  accentDark: '#E6BD00',     // Darker yellow for contrast
  
  // Neutral Colors
  white: '#FFFFFF',
  gray: '#AAAAAA',           // ISU Gray
  grayLight: '#F8F9FA',      // Light background
  grayDark: '#666666',       // Dark text
  
  // Text Colors
  text: {
    primary: '#000000',       // Black for main text
    secondary: '#666666',     // Dark gray for secondary text
    muted: '#AAAAAA',        // Gray for muted text
    onRed: '#FFFFFF',        // White text on red background
    onYellow: '#000000',     // Black text on yellow background
  },
  
  // Semantic Colors
  success: '#28a745',
  warning: '#F9D016',        // Use ISU Yellow for warnings
  error: '#CE1126',          // Use ISU Red for errors
  info: '#17a2b8',
  
  // UI Elements
  border: '#E5E5E5',
  shadow: 'rgba(206, 17, 38, 0.1)', // ISU Red with transparency
  hover: 'rgba(206, 17, 38, 0.05)',  // Very light red for hover
};

// Modern Button Styles with Spacing
export const BUTTON_STYLES = {
  primary: {
    background: COLORS.primary,
    color: COLORS.text.onRed,
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 2px 4px ${COLORS.shadow}`,
    '&:hover': {
      backgroundColor: COLORS.primaryDark,
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 8px ${COLORS.shadow}`,
    },
  },
  secondary: {
    background: COLORS.white,
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: COLORS.hover,
    },
  },
  outlined: {
    background: 'transparent',
    color: COLORS.primary,
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: COLORS.primary,
      color: COLORS.white,
    },
  },
  accent: {
    background: COLORS.accent,
    color: COLORS.text.onYellow,
    border: 'none',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: `0 2px 4px rgba(0,0,0,0.1)`,
    '&:hover': {
      backgroundColor: COLORS.accentDark,
    },
  },
};

// Common animations
export const ANIMATIONS = {
  spin: 'spin 1s linear infinite',
  bounce: 'gentle-bounce 2s ease-in-out infinite',
  fadeIn: 'fadeIn 0.3s ease-in',
};

// Modern Loading Component Styles
export const LOADING_STYLES = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    padding: '60px 20px',
    color: COLORS.text.secondary,
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: `3px solid ${COLORS.border}`,
    borderTop: `3px solid ${COLORS.primary}`,
    borderRadius: '50%',
    animation: ANIMATIONS.spin,
    marginBottom: '24px',
  },
  text: {
    fontSize: '16px',
    color: COLORS.text.secondary,
    textAlign: 'center',
  },
};

// Modern Layout & Spacing
export const LAYOUT = {
  maxWidth: '1200px',
  containerPadding: '24px',
  sectionSpacing: '80px',
  cardSpacing: '24px',
  
  // Responsive breakpoints
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px',
  },
  
  // Standard spacing scale (prioritizing white space)
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
    xxxl: '64px',
    huge: '96px',
  },
  
  // Border radius scale
  borderRadius: {
    sm: '6px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '50%',
  },
};

// Modern Card Styles
export const CARD_STYLES = {
  base: {
    backgroundColor: COLORS.white,
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${COLORS.border}`,
    transition: 'all 0.2s ease',
    overflow: 'hidden',
  },
  elevated: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${COLORS.border}`,
    transition: 'all 0.3s ease',
  },
  hover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  },
  padding: {
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '48px',
  },
};

// Page Container Styles (prioritizing white space)
export const PAGE_STYLES = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '48px 24px',
    minHeight: '100vh',
    backgroundColor: COLORS.grayLight,
  },
  header: {
    textAlign: 'center',
    marginBottom: '64px',
  },
  content: {
    backgroundColor: COLORS.white,
    borderRadius: '16px',
    padding: '48px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    border: `1px solid ${COLORS.border}`,
  },
  section: {
    marginBottom: '48px',
  },
};

// Typography Styles
export const TYPOGRAPHY = {
  heading1: {
    fontSize: '2.5rem',
    fontWeight: '700',
    lineHeight: '1.2',
    color: COLORS.text.primary,
    marginBottom: '24px',
  },
  heading2: {
    fontSize: '2rem',
    fontWeight: '600',
    lineHeight: '1.3',
    color: COLORS.text.primary,
    marginBottom: '20px',
  },
  heading3: {
    fontSize: '1.5rem',
    fontWeight: '600',
    lineHeight: '1.4',
    color: COLORS.text.primary,
    marginBottom: '16px',
  },
  heading4: {
    fontSize: '1.25rem',
    fontWeight: '600',
    lineHeight: '1.4',
    color: COLORS.text.primary,
    marginBottom: '12px',
  },
  body: {
    fontSize: '1rem',
    lineHeight: '1.6',
    color: COLORS.text.primary,
    marginBottom: '16px',
  },
  caption: {
    fontSize: '0.875rem',
    lineHeight: '1.5',
    color: COLORS.text.secondary,
    marginBottom: '12px',
  },
};

// Platform configuration
export const PLATFORM = {
  FEE_PERCENTAGE: 0.05, // 5% - Must match backend functions/stripe-endpoints.js
  NAME: 'Redbird Markets',
  EMAIL_DOMAIN: '@ilstu.edu',
};

// Export categories
export * from './categories';